import { handleModuleLoadingError } from './handleModuleLoadingError.js';
import { logError } from '../utils/logError/index.js';
import { parentElements } from '../utils/parentElements/index.js';
import { getSiblings } from '../utils/getSiblings/index.js';
import { sleep } from '../utils/sleep';

/**
 * @param {boolean} customErrorHandling pass TRUE to use custom error handling
 * @return {Promise<[PhotoSwipe]>}
 */
export const loadPhotoSwipe = (customErrorHandling) => {
    const promise = import('photoswipe/dist/photoswipe.esm.js').then(m => m.default);

    if (!customErrorHandling)
        promise.catch(handleModuleLoadingError);

    return promise;
};

/**
 * @param {boolean} customErrorHandling pass TRUE to use custom error handling
 * @return {Promise<[PhotoSwipeLightbox]>}
 */
export const loadPhotoSwipeLightbox = (customErrorHandling) => {
    const promise = import('photoswipe/dist/photoswipe-lightbox.esm.js').then(m => m.default);

    if (!customErrorHandling)
        promise.catch(handleModuleLoadingError);

    return promise;
};

/**
 * @param {boolean} customErrorHandling pass TRUE to use custom error handling
 * @return {Promise<[PrkPhotoImageAutoSize]>}
 */
export const loadPrkPhotoSwipeImageAutoSize = (customErrorHandling) => {
    const promise = import('/js/prk/photo-swipe-plugins/photo-swipe-image-auto-size/index.js').then(m => m.default);

    if (!customErrorHandling)
        promise.catch(handleModuleLoadingError);

    return promise;
};

/**
 * @param {boolean} customErrorHandling pass TRUE to use custom error handling
 * @return {Promise<[PrkPhotoSwipeThumbnails]>}
 */
export const loadPrkPhotoSwipeThumbnails = (customErrorHandling) => {
    const promise = import('/js/prk/photo-swipe-plugins/photo-swipe-thumbnails/index.js').then(m => m.default);

    if (!customErrorHandling)
        promise.catch(handleModuleLoadingError);

    return promise;
};

/**
 * @param {boolean} customErrorHandling pass TRUE to use custom error handling
 * @return {Promise<[PrkPhotoSwipeVideo]>}
 */
export const loadPrkPhotoSwipeVideo = (customErrorHandling) => {
    const promise = import('/js/prk/photo-swipe-plugins/photo-swipe-video/index.js').then(m => m.default);

    if (!customErrorHandling)
        promise.catch(handleModuleLoadingError);

    return promise;
};

/**
 * @param {Object} options
 * @return {Promise<PhotoSwipeLightbox>}
 */
const initPrkGallery = async function(options) {
    const [PhotoSwipe, PhotoSwipeLightbox, PrkPhotoSwipeImageAutoSize, PrkPhotoSwipeThumbnails] = await Promise.all([
        loadPhotoSwipe(),
        loadPhotoSwipeLightbox(),
        loadPrkPhotoSwipeImageAutoSize(),
        loadPrkPhotoSwipeThumbnails(),
    ]);

    const opts = Object.assign({}, options, {
        pswpModule: PhotoSwipe,
    });

    const lightbox = new PhotoSwipeLightbox(opts);
    new PrkPhotoSwipeImageAutoSize(lightbox);
    new PrkPhotoSwipeThumbnails(lightbox, {
        isMobile: document.querySelector('.mob#wrapper') !== null,
    });

    lightbox.init();
    return lightbox;
}

/**
 * @param {number} clickedIndex
 * @param {PhotoSwipeLightbox|null} lightbox
 * @param {Object} options
 * @return {Promise<PhotoSwipeLightbox>}
 */
const openPrkGallery = async function(clickedIndex, lightbox, options) {
    try {
        if (!lightbox) {
            lightbox = await initPrkGallery(options)
        }
        lightbox.loadAndOpen(clickedIndex);
    } catch (error) {
        logError(error);
    }

    return lightbox;
}

/**
 * Generic gallery
 * @param {HTMLElement[]} thumbElements
 * @param {Object} options
 */
export const PrkGallery = function(thumbElements, options = {}) {
    if (!(thumbElements instanceof Array) || !thumbElements.length)
        return;

    let lightbox = null;
    const defaultOptions = {
        gallery: thumbElements[0].parentElement,
        children: thumbElements,
    }
    options = Object.assign({}, defaultOptions, options);

    for (const thumb of thumbElements) {
        thumb.addEventListener('click', async (event) => {
            event.preventDefault();
            lightbox = await openPrkGallery(options.children.indexOf(event.currentTarget), lightbox, options);
        });
    }
}

let isAutoloadOfPrkGalleriesEnabled = false;
export const autoLoadPrkGalleries = function() {
    if (isAutoloadOfPrkGalleriesEnabled)
        return;

    isAutoloadOfPrkGalleriesEnabled = true;

    /**
     * @param {MouseEvent|PointerEvent|TouchEvent} event
     */
    document.addEventListener('click', async (event) => {
        const galleryThumbs = parentElements(event.target, (el) => el.hasAttribute('data-prk-gallery'));
        if (!galleryThumbs.length)
            return;

        event.preventDefault();
        event.stopPropagation();

        const clickedThumb = galleryThumbs[0];
        const galleryId = clickedThumb.dataset.prkGallery;
        const thumbs = getSiblings(
            clickedThumb,
            (el) => el.hasAttribute('data-prk-gallery') && el.dataset.prkGallery === galleryId
        );

        let lightbox = null;
        const options = {
            gallery: thumbs[0].parentElement,
            children: thumbs,
        }
        lightbox = await openPrkGallery(thumbs.indexOf(clickedThumb), lightbox, options);

        // Prevent memory leaks
        lightbox.on('close', async () => {
            await sleep(500);
            lightbox.destroy();
        });
    });
}
